import React from "react";
import Tile from "@src/components/containers/Tile";
import ProductImage from "@src/components/widgets/ProductImage";
import moment from "moment";
import ChildCutoffContainer from "@src/components/containers/ChildCutoffContainer";
import { Product } from "@src/types/product";
import DistributorLogo from "@src/components/common/DistributorLogo";
import { Button } from "@swordsweeper/ssi-ui-component-library";
import { Link } from "react-router-dom";

interface ProductsGroupedByAvailability {
  available: Product[];
  backordered: Product[];
  priceUnavailable: Product[];
  stockUnavailable: Product[];
}

interface CheckoutProductGroupingProps {
  distributorSlug: string;
  arrivesByDate: string;
  products: ProductsGroupedByAvailability;
  onPlaceOrder?: () => void;
  disabled?: boolean;
  checkoutBannerHtml?: string;
}

export default function CheckoutProductGrouping({
  distributorSlug,
  arrivesByDate,
  products,
  onPlaceOrder,
  disabled = false,
  checkoutBannerHtml = "",
}: CheckoutProductGroupingProps) {
  const availableProducts = products.available;
  const backorderedProducts = products.backordered;

  const bannerContent = checkoutBannerHtml ? (
    <div
      className="checkout-disclaimer-banner"
      dangerouslySetInnerHTML={{ __html: checkoutBannerHtml }}
    />
  ) : undefined;

  return (
    <Tile
      data-testid={`grouping-${distributorSlug}`}
      headerContent={bannerContent}
    >
      <div className="flex-row space-between padding-bottom-xxl padding-top-sm">
        <div className="flex-row spaced-content-lg flex-center">
          <DistributorLogo distributorSlug={distributorSlug} height={24} />

          <span
            className="text-secondary"
            data-testid={`arrival-date-${distributorSlug}`}
          >
            <span>Arrives by {moment(arrivesByDate).format("ddd, MMM D")}</span>
          </span>
        </div>

        {onPlaceOrder && (
          <Button
            label="Place My Order"
            size="small"
            variant="checkout"
            showLabel
            className="btn-sm"
            onClick={disabled ? undefined : onPlaceOrder}
            disabled={disabled || (availableProducts.length === 0 && backorderedProducts.length === 0)}
            data-testid={`place-order-button-${distributorSlug}`}
          />
        )}
      </div>

      <ChildCutoffContainer
        rootClassName="padding-bottom-md"
        childContainerClassName="flex-row spaced-content-lg"
      >
        {availableProducts?.map((cartProduct) => {
          const { product, imageUrl, thumbnailUrl } = cartProduct as Product & {
            product: Product;
            imageUrl?: string;
            thumbnailUrl?: string;
          };
          const imageSrc = thumbnailUrl ?? imageUrl ?? product.thumbnailUrl;
          return (
            <Link
              key={`checkout-product-${product.id}`}
              data-testid={`checkout-product-image-link-${product.id}`}
              to={`/product/${product.id}`}
            >
              <ProductImage imageSrc={imageSrc} />
            </Link>
          );
        })}
      </ChildCutoffContainer>

      {backorderedProducts?.length > 0 && (
        <div className="margin-top-md padding-top-xs flex-row space-between border-top">
          <div className="flex-row flex-center">
            <span className="margin-right-md text-xs text-secondary">
              +{backorderedProducts.length} backordered item
              {backorderedProducts.length === 1 ? "" : "s"}
            </span>
            {backorderedProducts.map((cartProduct) => {
              const { product, imageUrl, thumbnailUrl } =
                cartProduct as Product & {
                  product: Product;
                  imageUrl?: string;
                  thumbnailUrl?: string;
                };
              const imageSrc =
                thumbnailUrl ?? imageUrl ?? product.thumbnailUrl;
              return (
                <React.Fragment key={`backordered-product-${product.id}`}>
                  <ProductImage imageSrc={imageSrc} sizePx={40} />
                </React.Fragment>
              );
            })}
          </div>

          <span className="text-xs text-secondary">
            We'll notify you when shipped.
          </span>
        </div>
      )}
    </Tile>
  );
}
